/**
 * 🎯 ZONE-BASED SELECTORS
 * 
 * Clean, isolated selectors for each zone that prevent cross-zone contamination.
 * These selectors ensure that components only subscribe to their specific zone data.
 */

import { createSelector } from '@reduxjs/toolkit';

// ===== BASE ZONE SELECTORS =====
export const selectCharacterZone = state => state.user.bookWorksheetData?.characterZone || { characters: [], selectedCharacter: null, loading: false, error: null, lastUpdated: null };
export const selectVariationZone = state => state.user.bookWorksheetData?.variationZone || { variations: [], activeVariation: null, activeVersion: null, samples: [], variationCounts: {}, loading: false, error: null, lastUpdated: null, editMode: 'view', hasUnsavedChanges: false, isSubmitting: false };
export const selectSharedData = state => state.user.bookWorksheetData?.sharedData || { litGroups: [], books: [], litGroupProfiles: {}, bookProfiles: {}, selections: { litGroupId: null, bookId: null, characterId: null, variationId: null }, loading: { litGroups: false, books: false, profiles: false }, errors: { litGroups: null, books: null, profiles: null }, lastUpdated: { litGroups: null, books: null, profiles: null } };

// ===== CHARACTER ZONE SELECTORS (PURE) =====
export const selectCharacters = createSelector(
  [selectCharacterZone],
  (characterZone) => characterZone.characters
);

export const selectSelectedCharacter = createSelector(
  [selectCharacterZone],
  (characterZone) => characterZone.selectedCharacter
);

export const selectCharacterZoneLoading = createSelector(
  [selectCharacterZone],
  (characterZone) => characterZone.loading
);

export const selectCharacterZoneError = createSelector(
  [selectCharacterZone],
  (characterZone) => characterZone.error
);

export const selectCharacterZoneLastUpdated = createSelector(
  [selectCharacterZone],
  (characterZone) => characterZone.lastUpdated
);

// Character by ID selector
export const selectCharacterById = createSelector(
  [selectCharacters, (state, characterId) => characterId],
  (characters, characterId) => characters.find(char => char.uuid === characterId) || null
);

// ===== VARIATION ZONE SELECTORS (PURE) =====
export const selectVariations = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone.variations
);

export const selectActiveVariation = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone.activeVariation
);

export const selectActiveVersion = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone.activeVersion
);

export const selectSamples = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone.samples
);

export const selectVariationCounts = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone.variationCounts
);

export const selectVariationZoneLoading = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone.loading
);

export const selectVariationZoneError = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone.error
);

export const selectEditMode = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone.editMode
);

export const selectHasUnsavedChanges = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone.hasUnsavedChanges
);

export const selectIsSubmitting = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone.isSubmitting
);

// Variation by ID selector
export const selectVariationById = createSelector(
  [selectVariations, (state, variationId) => variationId],
  (variations, variationId) => variations.find(variation => variation.uuid === variationId) || null
);

// Sample by ID selector
export const selectSampleById = createSelector(
  [selectSamples, (state, sampleId) => sampleId],
  (samples, sampleId) => samples.find(sample => sample.uuid === sampleId) || null
);

// Active sample selector
export const selectActiveSample = createSelector(
  [selectSamples, selectActiveVariation],
  (samples, activeVariation) => {
    if (!activeVariation?.activeSampleUuid) return null;
    return samples.find(sample => sample.uuid === activeVariation.activeSampleUuid) || null;
  }
);

// ===== SHARED DATA SELECTORS (READ-ONLY) =====
export const selectLitGroups = createSelector(
  [selectSharedData],
  (sharedData) => sharedData.litGroups
);

export const selectBooks = createSelector(
  [selectSharedData],
  (sharedData) => sharedData.books
);

export const selectLitGroupProfiles = createSelector(
  [selectSharedData],
  (sharedData) => sharedData.litGroupProfiles
);

export const selectBookProfiles = createSelector(
  [selectSharedData],
  (sharedData) => sharedData.bookProfiles
);

export const selectSelections = createSelector(
  [selectSharedData],
  (sharedData) => sharedData.selections
);

export const selectSelectedLitGroupId = createSelector(
  [selectSelections],
  (selections) => selections.litGroupId
);

export const selectSelectedBookId = createSelector(
  [selectSelections],
  (selections) => selections.bookId
);

export const selectSelectedCharacterId = createSelector(
  [selectSelections],
  (selections) => selections.characterId
);

export const selectSelectedVariationId = createSelector(
  [selectSelections],
  (selections) => selections.variationId
);

export const selectSharedDataLoading = createSelector(
  [selectSharedData],
  (sharedData) => sharedData.loading
);

export const selectSharedDataErrors = createSelector(
  [selectSharedData],
  (sharedData) => sharedData.errors
);

// Specific profile selectors
export const selectLitGroupProfileById = createSelector(
  [selectLitGroupProfiles, (state, litGroupId) => litGroupId],
  (profiles, litGroupId) => profiles[litGroupId] || null
);

export const selectBookProfileById = createSelector(
  [selectBookProfiles, (state, bookId) => bookId],
  (profiles, bookId) => profiles[bookId] || null
);

// Current selections with data
export const selectCurrentLitGroup = createSelector(
  [selectLitGroups, selectSelectedLitGroupId],
  (litGroups, selectedId) => {
    if (!selectedId) return null;
    return litGroups.find(group => group.uuid === selectedId) || null;
  }
);

export const selectCurrentBook = createSelector(
  [selectBooks, selectSelectedBookId, selectBookProfiles],
  (books, selectedId, bookProfiles) => {
    if (!selectedId) return null;
    const book = books.find(book => book.uuid === selectedId);
    if (!book) return null;

    // Merge profile data - ALL form data stays in profile (correct architecture)
    const profileData = bookProfiles[selectedId];
    if (profileData) {
      return {
        ...book,           // ✅ Book node base data (uuid, name, aaiType, authors, etc.)
        // ALL profile data stays nested in profile (correct architecture)
        profile: {
          // Form data - ALL belongs in BookProfile node
          priority: profileData.priority,
          status: profileData.status,
          deadline: profileData.deadline,
          bookCover: profileData.bookCover,
          description: profileData.description,
          author: profileData.author,
          authorFirstName: profileData.authorFirstName,
          authorLastName: profileData.authorLastName,
          series: profileData.series,
          // Include any other profile data
          ...profileData
        }
      };
    }

    return book;
  }
);

export const selectCurrentCharacter = createSelector(
  [selectCharacters, selectSelectedCharacterId],
  (characters, selectedId) => {
    if (!selectedId) return null;
    return characters.find(char => char.uuid === selectedId) || null;
  }
);

export const selectCurrentVariation = createSelector(
  [selectVariations, selectSelectedVariationId],
  (variations, selectedId) => {
    if (!selectedId) return null;
    return variations.find(variation => variation.uuid === selectedId) || null;
  }
);

// ===== COMPOSITE SELECTORS (CONTROLLED CROSS-ZONE ACCESS) =====

// Character zone data with minimal shared data
export const selectCharacterZoneData = createSelector(
  [selectCharacters, selectSelectedCharacter, selectCharacterZoneLoading, selectCharacterZoneError, selectLitGroups, selectBooks],
  (characters, selectedCharacter, loading, error, litGroups, books) => ({
    characters,
    selectedCharacter,
    loading,
    error,
    // Minimal shared data needed for character operations
    litGroups,
    books
  })
);

// Variation zone data with minimal shared data
export const selectVariationZoneData = createSelector(
  [
    selectVariations,
    selectActiveVariation,
    selectActiveVersion,
    selectSamples,
    selectVariationCounts,
    selectVariationZoneLoading,
    selectVariationZoneError,
    selectEditMode,
    selectHasUnsavedChanges,
    selectIsSubmitting,
    selectSelectedCharacterId
  ],
  (
    variations,
    activeVariation,
    activeVersion,
    samples,
    variationCounts,
    loading,
    error,
    editMode,
    hasUnsavedChanges,
    isSubmitting,
    characterId
  ) => ({
    variations,
    activeVariation,
    activeVersion,
    samples,
    variationCounts,
    loading,
    error,
    editMode,
    hasUnsavedChanges,
    isSubmitting,
    // Minimal shared data needed for variation operations
    characterId
  })
);

// Shared data for components that need read-only access
export const selectSharedDataForComponents = createSelector(
  [
    selectLitGroups,
    selectBooks,
    selectLitGroupProfiles,
    selectBookProfiles,
    selectSelections,
    selectSharedDataLoading,
    selectSharedDataErrors
  ],
  (litGroups, books, litGroupProfiles, bookProfiles, selections, loading, errors) => ({
    litGroups,
    books,
    litGroupProfiles,
    bookProfiles,
    selections,
    loading,
    errors
  })
);

// ===== PERFORMANCE OPTIMIZATION SELECTORS =====

// Memoized character count
export const selectCharacterCount = createSelector(
  [selectCharacters],
  (characters) => characters.length
);

// Memoized variation count
export const selectVariationCount = createSelector(
  [selectVariations],
  (variations) => variations.length
);

// Memoized sample count
export const selectSampleCount = createSelector(
  [selectSamples],
  (samples) => samples.length
);

// Check if any zone is loading
export const selectAnyZoneLoading = createSelector(
  [selectCharacterZoneLoading, selectVariationZoneLoading, selectSharedDataLoading],
  (characterLoading, variationLoading, sharedLoading) =>
    characterLoading || variationLoading ||
    sharedLoading.litGroups || sharedLoading.books || sharedLoading.profiles
);

// Check if any zone has errors
export const selectAnyZoneError = createSelector(
  [selectCharacterZoneError, selectVariationZoneError, selectSharedDataErrors],
  (characterError, variationError, sharedErrors) =>
    characterError || variationError ||
    sharedErrors.litGroups || sharedErrors.books || sharedErrors.profiles
);

// ===== LEGACY COMPATIBILITY SELECTORS =====
// These provide backward compatibility during migration

export const selectCharactersLegacy = selectCharacters;
export const selectVariationsLegacy = selectVariations;
export const selectLitGroupsLegacy = selectLitGroups;
export const selectBooksLegacy = selectBooks;
